import { Table, Tag } from "antd";
import usePublications from "hooks/usePublications";
import { useEffect, useMemo, useState } from "react";
import { getFullDay, getFullMonth, getNameFromObject } from "utils/Utils";
import { routesNames } from "utils/Constants";
import { useNavigate } from "react-router-dom";
import { niveles } from "utils/Tables";
import { Link } from "react-router-dom";
import { Input } from "antd";
import { BiSearchAlt } from "react-icons/bi";
import ExportExcel from "components/excel/ExportExcel";
import dayjs from 'dayjs';
import { getTree } from "services/Especialidades";
import { getEstadosPublicaciones } from 'services/Estados';


const DEFAULT_COLUMN_FILTERS = {
    cuenta: [],
    plan: [],
    privada: [],
    nivel: [],
    estado: [],
};

// No hay un campo "republicada": al republicar se corre el vencimiento pero
// created_at no cambia. Una publicación normal dura ~30 días, así que una
// duración mayor a 45 días entre creación y vencimiento implica republicación.
const REPUBLICADA_MIN_DIAS = 45;
const isRepublicada = (item) =>
    !!item?.created_at && !!item?.vencimiento &&
    dayjs(item.vencimiento).diff(dayjs(item.created_at), 'day') > REPUBLICADA_MIN_DIAS;

const PublicationTable = ({ user = null }) => {
    let navigate = useNavigate();
    const [allPublications, setAllPublications] = useState([]);
    const [tree, setTree] = useState(null);
    const [value, setValue] = useState("")
    const { isLoading, fetchData } = usePublications()
    const [cuentaOptions, setCuentaOptions] = useState([]);
    const [columnFilters, setColumnFilters] = useState(DEFAULT_COLUMN_FILTERS);
    const [estados, setEstados] = useState([])

    const getEstados = async () => {
        const data = await getEstadosPublicaciones();
        const estados = data.map(item => ({
            value: item.id,
            text: item.nombre
        }));

        setEstados(estados);
    };


    const getState = (item) => {
        const vencimiento = new Date(item.vencimiento);
        const fecha = new Date();
        let vencida = false;

        if (fecha > vencimiento) {
            vencida = true
        }

        if (vencida) {
            return "Vencida"
        }

        if (item.activa == 0) {
            return "Desactivada"
        }

        if (item.estado) {
            return item.estado_publicacion.nombre
        } else {
            return ""
        }
    }

    const buildExcelRows = (data, especialidades) => {
        return Object.values(data)?.map((item) => {
            return {
                Estado: getState(item),
                Nombre: item.nombre,
                Cuenta: item.cuenta?.nombre,
                Referencia: item.referencia,
                Especialidad: especialidades?.tree?.tag[item.especialidad_id],
                Liderazgo: item.liderazgo ? "Si" : "No",
                Visibilidad: item.privada ? "Confidencial" : "Pública",
                Creada: dayjs(item.created_at).format('DD/MM/YYYY'),
                Vencimiento: dayjs(item.vencimiento).format('DD/MM/YYYY'),
                Nivel: getNameFromObject(niveles, item.nivel),
                Jornada: item.jornada,
                Posicion: item.posicion,
                Turno: item.turno === 'n' ? 'Noche' : item.turno === 'm' ? 'Mañana' : 'Tarde',
                Visitas: item.visitas_count,
            };
        });
    };


    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
            key: 'nombre',
            render: (text, record) => (
                <div className="flex flex-col">
                    <span className="text-sm" dangerouslySetInnerHTML={{ __html: text.length > 50 ? text.slice(0, 50) : text }}></span>
                    {isRepublicada(record) && (
                        <span className="text-[11px] font-semibold leading-tight text-red-500">Republicación</span>
                    )}
                </div>
            ),
        },
        {
            title: 'Cuenta',
            dataIndex: 'cuenta',
            key: 'cuenta',
            render: (text, record) => {
                return <Link to={`${routesNames.USER}/${record?.cuenta?.user_id}`} className="text-blue-700">{record?.cuenta?.nombre}</Link>;
            },
            filters: cuentaOptions,
            filteredValue: columnFilters.cuenta,
        },
        {
            title: 'Plan',
            dataIndex: 'plan',
            key: 'plan',
            render: (text, record) => {
                return (
                    <Tag color={record.cuenta?.user?.plan?.detalle?.id === 1 ? "gold" : "purple"}>
                        {record.cuenta?.user?.plan?.detalle?.id === 1 ? "Gold" : "Standard"}
                    </Tag>
                );
            },
            filters: [
                {
                    text: 'Gold',
                    value: 1,
                },
                {
                    text: 'Standard',
                    value: 2,
                },
            ],
            filteredValue: columnFilters.plan,
        },
        {
            title: 'Visibilidad',
            dataIndex: 'privada',
            key: 'privada',
            render: (text, record) => {
                if (record.privada == 0) {
                    return <Tag className="bg-[#34d399] text-white" >Pública</Tag>
                } else {
                    return <Tag className="bg-cancelado text-white" >Confidencial</Tag>
                }
            },

            filters: [
                {
                    text: 'Pública',
                    value: 0,
                },
                {
                    text: 'Confidencial',
                    value: 1,
                },
            ],
            filteredValue: columnFilters.privada,
        },
        {
            title: 'Fecha',
            dataIndex: 'created_at',
            render: (text) => {
                const date = new Date(text);
                return `${getFullDay(date, true)}/${getFullMonth(date)}/${date.getFullYear()}`
            },
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at)
        },
        {
            title: 'Vencimiento',
            dataIndex: 'vencimiento',
            render: (text) => {
                const date = new Date(text);
                return `${getFullDay(date, true)}/${getFullMonth(date)}/${date.getFullYear()}`
            },
            sorter: (a, b) => new Date(a.vencimiento) - new Date(b.vencimiento)
        },
        {
            title: 'Nivel',
            dataIndex: 'nivel',
            render: (text) => {
                return getNameFromObject(niveles, text)
            },
            filters: niveles.map((n) => {
                return {
                    text: n.name,
                    value: n.value
                }
            }),
            filteredValue: columnFilters.nivel,
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            render: (text, record) => {
                return <Tag style={{ backgroundColor: record?.estado_publicacion?.color, color: "white" }}>
                    {record?.estado_publicacion?.nombre}
                </Tag>
            },
            filters: estados,
            filteredValue: columnFilters.estado,
        },
        {
            title: 'Detalle',
            dataIndex: 'detalle',
            render: (text, record) => {
                return <button onClick={() => navigate(`${routesNames.PUBLICATION}/${record.id}`)} className="bg-main text-sm w-full rounded-xl">Ver</button>
            }
        },
    ];

    const fetchPublications = async () => {
        const treeResponse = await getTree()
        const data = await fetchData(null, { userId: user }, true)

        // Orden por vencimiento descendente
        // Se filtran entradas que no son publicaciones reales (ej: el objeto "plan"
        // que la API devuelve mezclado en el mismo listado)
        const sortedData = Object.values(data?.data)
            .filter((item) => item?.created_at)
            .sort((a, b) =>
                new Date(b.vencimiento) - new Date(a.vencimiento)
            );

        setAllPublications(sortedData)
        setTree(treeResponse)
    }

    useEffect(() => {
        fetchPublications()
        getEstados()
    }, [])

    useEffect(() => {
        if (allPublications.length > 0) {
            const uniqueAccounts = Array.from(new Set(allPublications.map((item) => item.cuenta?.nombre)))
                .filter((name) => name !== undefined) // Excluye undefined si hay cuentas sin nombre
                .map((name) => ({
                    text: name,
                    value: name
                }));

            setCuentaOptions(uniqueAccounts);
        }
    }, [allPublications]);

    // Filtrado combinado: buscador (texto libre) + filtros de columnas, todo sobre los datos
    // ya cargados en el navegador.
    const filteredData = useMemo(() => {
        const search = value.trim().toLowerCase();

        return allPublications.filter((entry) => {
            if (search) {
                const matchesSearch =
                    entry.cuenta?.nombre?.toLowerCase().includes(search) ||
                    entry.nombre?.toLowerCase().includes(search) ||
                    entry.nivel?.toLowerCase().includes(search);

                if (!matchesSearch) return false;
            }

            if (columnFilters.cuenta.length > 0 && !columnFilters.cuenta.includes(entry.cuenta?.nombre)) return false;
            if (columnFilters.plan.length > 0 && !columnFilters.plan.includes(entry.cuenta?.user?.plan?.detalle?.id)) return false;
            if (columnFilters.privada.length > 0 && !columnFilters.privada.some((v) => v == entry.privada)) return false;
            if (columnFilters.nivel.length > 0 && !columnFilters.nivel.some((v) => v == entry.nivel)) return false;
            if (columnFilters.estado.length > 0 && !columnFilters.estado.includes(entry.estado_id)) return false;

            return true;
        });
    }, [allPublications, value, columnFilters]);

    const hasActiveFilters =
        value.trim().length > 0 ||
        Object.values(columnFilters).some((arr) => arr.length > 0);

    const dataExcelFiltered = useMemo(() => buildExcelRows(filteredData, tree), [filteredData, tree]);
    const dataExcelAll = useMemo(() => buildExcelRows(allPublications, tree), [allPublications, tree]);

    return (
        <>
            <Input
                allowClear
                placeholder="Buscar por Cuenta,Nombre de publicación o nivel"
                prefix={<BiSearchAlt className="text-xl" />}
                value={value}
                size="small"
                className="p-3 w-full rounded-lg mb-2"
                onChange={e => setValue(e.target.value)}
            />

            <div className="flex flex-row justify-between items-center mb-3">
                <span className="block text-sm font-semibold text-start my-1 me-5">
                    {filteredData?.length} publicaciones encontradas
                </span>
                <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                        <ExportExcel data={dataExcelFiltered} fileName={"Publicaciones_resultados_" + new Date().toLocaleDateString()} title="Exportar resultados" />
                    )}
                    <ExportExcel data={dataExcelAll} fileName={"Publicaciones_" + new Date().toLocaleDateString()} title="Exportar todo" />
                </div>
            </div>

            <Table
                loading={isLoading}
                dataSource={filteredData}
                columns={columns}
                className="w-full"
                rowKey={(record) => record.id}
                size="small"
                pagination={
                    {
                        pageSize: 20,
                        showSizeChanger: false
                    }
                }
                locale={{ emptyText: "No hay resultados" }}
                onChange={(pagination, tableFilters, sorter, extra) => {
                    if (extra.action === "filter") {
                        setColumnFilters({
                            cuenta: tableFilters.cuenta || [],
                            plan: tableFilters.plan || [],
                            privada: tableFilters.privada || [],
                            nivel: tableFilters.nivel || [],
                            estado: tableFilters.estado || [],
                        });
                    }
                }}
            />
        </>
    )
};

export default PublicationTable;
